/**
 * EditorDrawer - Slides in from the right over the main content area.
 * Does NOT use a portal - renders inline with absolute positioning.
 * Parent must have `position: relative` for proper positioning.
 */
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface EditorDrawerProps {
	/** Whether the drawer is visible */
	isOpen: boolean;
	/** Content to render inside the drawer */
	children: ReactNode;
}

/**
 * A drawer that slides in from the right over the parent container.
 * Parent must have `position: relative` for proper positioning.
 * Always renders in DOM to enable smooth enter/exit animations.
 * @param props - Component props
 * @param props.isOpen - Whether the drawer is visible
 * @param props.children - Content to render inside the drawer
 */
export function EditorDrawer({ isOpen, children }: EditorDrawerProps) {
	const drawerRef = useRef<HTMLDivElement>(null);

	// Focus management: focus the drawer when it opens
	useEffect(() => {
		if (isOpen && drawerRef.current) {
			const timer = setTimeout(() => {
				drawerRef.current?.focus();
			}, 50);
			return () => { clearTimeout(timer); };
		}
	}, [isOpen]);

	return (
		<div
			ref={drawerRef}
			tabIndex={isOpen ? -1 : undefined}
			className={cn(
				// Base positioning - fills parent container
				"absolute inset-0 z-50 bg-background",
				// Left border and shadow (visible edge when sliding from right)
				"border-l shadow-[-8px_0_16px_-4px_rgba(0,0,0,0.15)]",
				// Animation: slide in/out from right (using standard Tailwind classes)
				"transition-transform duration-500 ease-in-out",
				isOpen ? "translate-x-0" : "translate-x-full"
			)}
			role="dialog"
			aria-modal={isOpen}
			aria-hidden={!isOpen}
		>
			{children}
		</div>
	);
}
